import { useOffline } from "@/contexts/OfflineContext";
import { Wifi, WifiOff, RefreshCw, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Subtle pill in the top app bar showing online/offline + pending sync count.
 * Hidden completely when online with no pending actions, to avoid noise.
 */
export default function OfflineStatusBadge() {
  const { online, pending, syncing, lastSyncResult, syncNow } = useOffline();
  const [showJustSynced, setShowJustSynced] = useState(false);

  useEffect(() => {
    if (lastSyncResult?.ok > 0) {
      setShowJustSynced(true);
      const t = setTimeout(() => setShowJustSynced(false), 4000);
      return () => clearTimeout(t);
    }
  }, [lastSyncResult]);

  if (online && pending === 0 && !syncing && !showJustSynced) return null;

  let cls = "bg-slate-100 text-slate-700 border-slate-300";
  let icon = <Wifi className="h-3.5 w-3.5" />;
  let label = "";
  let onClick = null;
  let testid = "offline-badge-online";

  if (!online) {
    cls = "bg-amber-100 text-amber-900 border-amber-300";
    icon = <WifiOff className="h-3.5 w-3.5" />;
    label = pending > 0 ? `Offline · ${pending} tertunda` : "Offline";
    testid = "offline-badge-offline";
  } else if (syncing) {
    cls = "bg-blue-100 text-blue-900 border-blue-300";
    icon = <RefreshCw className="h-3.5 w-3.5 animate-spin" />;
    label = "Sinkronisasi…";
    testid = "offline-badge-syncing";
  } else if (pending > 0) {
    cls = "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 cursor-pointer";
    icon = <RefreshCw className="h-3.5 w-3.5" />;
    label = `${pending} tertunda — sync`;
    onClick = syncNow;
    testid = "offline-badge-pending";
  } else if (showJustSynced) {
    cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
    icon = <CheckCircle2 className="h-3.5 w-3.5" />;
    label = `${lastSyncResult.ok} tersinkron`;
    testid = "offline-badge-synced";
  }

  return (
    <button
      type="button"
      onClick={onClick || undefined}
      disabled={!onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition ${cls} ${onClick ? "" : "cursor-default"}`}
      title={label}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
